import { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../../utils/helpers';
import { getPublicStats, subscribeToPublicStats } from '../../services/firestoreService';

const LiveStats = () => {
  const [stats, setStats] = useState({
    totalRegistrations: 0,
    totalTokensSold: 0,
    totalRevenue: 0,
    totalWinners: 0,
    armyFundDonation: 0,
    approvedUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const applyStats = useCallback((data) => {
    if (!data) {
      setLoading(false);
      return;
    }

    setStats({
      totalRegistrations: data.totalRegistrations || 0,
      totalTokensSold: data.totalTokensSold || 0,
      totalRevenue: data.totalRevenue || 0,
      totalWinners: data.totalWinners || 0,
      armyFundDonation: data.armyFundDonation || 0,
      approvedUsers: data.approvedUsers || 0
    });

    if (data.updatedAt?.toDate) {
      setLastUpdate(data.updatedAt.toDate());
    } else if (typeof data.updatedAt === 'string') {
      setLastUpdate(new Date(data.updatedAt));
    } else {
      setLastUpdate(new Date());
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToPublicStats(
      (snapshotData) => {
        applyStats(snapshotData);
      },
      async () => {
        const result = await getPublicStats();
        if (result.success) {
          applyStats(result.data);
        } else {
          setLoading(false);
        }
      }
    );

    // Initial fetch (in case listener returns null or doc missing)
    (async () => {
      const result = await getPublicStats();
      if (result.success) {
        applyStats(result.data);
      } else {
        setLoading(false);
      }
    })();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [applyStats]);

  return (
    <section className="section section-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '3rem 0', position: 'relative' }}>
      <div className="container">
        <div className="section-header" style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <h2 className="section-title" style={{ color: 'white', fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎯 Live VIP Customer Statistics</h2>
            <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem' }}>Real-time platform statistics • Updates automatically</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Last updated: {lastUpdate.toLocaleTimeString()}
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <a
              href="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#fbbf24',
                color: '#1f2937',
                fontWeight: 700,
                padding: '0.75rem 1.5rem',
                borderRadius: '999px',
                boxShadow: '0 10px 20px rgba(251, 191, 36, 0.35)',
                textDecoration: 'none'
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>login</span>
              Login
            </a>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <div className="stat-card" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.2)', transition: 'transform 0.3s ease', cursor: 'pointer', minHeight: '180px' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <i data-lucide="users" style={{ color: '#fbbf24', width: '40px', height: '40px', marginBottom: '0.75rem' }}></i>
            <h3 style={{ color: 'white', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', lineHeight: '1' }}>
              {loading ? '...' : stats.totalRegistrations}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', fontWeight: '600', textAlign: 'center', marginBottom: '0.25rem' }}>Total Registrations</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', textAlign: 'center' }}>
              {stats.approvedUsers} Approved
            </p>
          </div>
          
          <div className="stat-card" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.2)', transition: 'transform 0.3s ease', cursor: 'pointer', minHeight: '180px' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <i data-lucide="ticket" style={{ color: '#fbbf24', width: '40px', height: '40px', marginBottom: '0.75rem' }}></i>
            <h3 style={{ color: 'white', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', lineHeight: '1' }}>
              {loading ? '...' : stats.totalTokensSold}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', fontWeight: '600', textAlign: 'center', marginBottom: '0.25rem' }}>VIP Tokens Issued</p>
          </div>
          
          <div className="stat-card" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.2)', transition: 'transform 0.3s ease', cursor: 'pointer', minHeight: '180px' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <i data-lucide="indian-rupee" style={{ color: '#fbbf24', width: '40px', height: '40px', marginBottom: '0.75rem' }}></i>
            <h3 style={{ color: 'white', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', lineHeight: '1', wordBreak: 'break-word' }}>
              {loading ? '...' : formatCurrency(stats.totalRevenue)}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', fontWeight: '600', textAlign: 'center', marginBottom: '0.25rem' }}>Winning Price</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', textAlign: 'center' }}>
              From token sales
            </p>
          </div>
          
          <div className="stat-card" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.2)', transition: 'transform 0.3s ease', cursor: 'pointer', minHeight: '180px' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <i data-lucide="trophy" style={{ color: '#fbbf24', width: '40px', height: '40px', marginBottom: '0.75rem' }}></i>
            <h3 style={{ color: 'white', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', lineHeight: '1' }}>
              {loading ? '...' : stats.totalWinners}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', fontWeight: '600', textAlign: 'center', marginBottom: '0.25rem' }}>Total Winners</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', textAlign: 'center' }}>
              Real winners, real prizes
            </p>
          </div>
          
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(22, 163, 74, 0.2) 100%)', backdropFilter: 'blur(10px)', borderRadius: '12px', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(34, 197, 94, 0.3)', transition: 'transform 0.3s ease', cursor: 'pointer', minHeight: '180px' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <i data-lucide="heart" style={{ color: '#22c55e', width: '40px', height: '40px', marginBottom: '0.75rem' }}></i>
            <h3 style={{ color: 'white', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', lineHeight: '1', wordBreak: 'break-word' }}>
              {loading ? '...' : formatCurrency(stats.armyFundDonation)}
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', fontWeight: '600', textAlign: 'center', marginBottom: '0.25rem' }}>Army Fund Donation</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', textAlign: 'center' }}>
              Supporting our heroes 🇮🇳
            </p>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.1)', padding: '0.75rem 1.5rem', borderRadius: '50px', border: '1px solid rgba(255,255,255,0.2)' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite' }}></div>
            <span style={{ color: 'white', fontSize: '0.875rem', fontWeight: '600' }}>Live Updates • Auto-refreshing</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveStats;
